/**
 * AI Chat Store
 *
 * Zustand store for AI chat conversation state management with persistence.
 * Manages conversation list, active conversation, messages, and UI state.
 */

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { v4 as uuidv4 } from 'uuid';
import {
  conversationApi,
  ConversationStatus,
  MessageRole,
} from '@/services/api/conversationApi';
import type {
  Conversation,
  ConversationSummary,
  AIMessage,
} from '@/services/api/conversationApi';
import type {
  ClarificationRequest,
  ClarificationResponse,
  ClarificationHistoryEntry,
} from '@/types/ai';

/**
 * Phase 7.2: Command History Entry
 *
 * Tracks executed commands for replay functionality and history panel.
 * Each entry captures the command, its context, and outcome.
 */
export interface CommandHistoryEntry {
  /** Unique identifier for this history entry */
  id: string;
  /** The command text that was executed */
  command: string;
  /** Timestamp when the command was executed */
  timestamp: number;
  /** Type of component the command was applied to */
  componentType: string;
  /** Whether the command executed successfully */
  success: boolean;
  /** Optional property changes that were applied */
  propertyChanges?: Record<string, unknown>;
  /** Optional component ID that was targeted */
  componentId?: string;
}

// Maximum number of commands to keep in history
const MAX_COMMAND_HISTORY = 50;

// Re-export types for convenience
export { ConversationStatus, MessageRole };
export type { Conversation, ConversationSummary };

/**
 * Chat message interface for frontend use
 * Extended from AIMessage with additional UI properties
 */
export interface ChatMessage {
  id: string;
  content: string;
  role: 'user' | 'assistant' | 'system';
  timestamp: Date;
  isStreaming?: boolean;
  context?: string;
  componentData?: any;
  layoutData?: any;
  applied?: boolean;
  propertyChanges?: any;
  metadata?: Record<string, any>;
}

/**
 * AI Chat Store State
 */
interface AIChatState {
  // Conversation list
  conversations: ConversationSummary[];
  isLoadingConversations: boolean;
  conversationsError: string | null;

  // Active conversation
  activeConversationId: string | null;
  activeConversation: Conversation | null;
  messages: ChatMessage[];
  isLoadingMessages: boolean;

  // UI state
  inputValue: string;
  isTyping: boolean;
  error: string | null;
  contextEnabled: boolean;
  showConversationList: boolean;

  // Sync state
  isSyncing: boolean;
  lastSyncedAt: Date | null;
  pendingMessages: ChatMessage[];

  // Clarification state (Phase 1.2)
  pendingClarification: ClarificationRequest | null;
  clarificationHistory: ClarificationHistoryEntry[];
  isAwaitingClarification: boolean;

  // Actions - Conversation Management
  loadConversations: (projectId?: string) => Promise<void>;
  loadMoreConversations: () => Promise<void>;
  createConversation: (title?: string, projectId?: string) => Promise<string>;
  switchConversation: (id: string) => Promise<void>;
  clearActiveConversation: () => void;
  deleteConversation: (id: string) => Promise<void>;
  updateConversationTitle: (id: string, title: string) => Promise<void>;
  archiveConversation: (id: string) => Promise<void>;

  // Actions - Message Management
  addMessage: (message: Omit<ChatMessage, 'id' | 'timestamp'>) => ChatMessage;
  updateMessage: (id: string, updates: Partial<ChatMessage>) => void;
  removeMessage: (id: string) => void;
  syncMessages: () => Promise<void>;
  saveMessageToServer: (message: ChatMessage) => Promise<void>;

  // Actions - UI State
  setInputValue: (value: string) => void;
  setError: (error: string | null) => void;
  setContextEnabled: (enabled: boolean) => void;
  setIsTyping: (isTyping: boolean) => void;
  toggleConversationList: () => void;
  clearMessages: () => void;

  // Actions - Initialization
  initialize: (projectId?: string) => Promise<void>;
  resumeActiveConversation: (projectId?: string) => Promise<void>;

  // Actions - Clarification (Phase 1.2)
  setPendingClarification: (request: ClarificationRequest | null) => void;
  clearPendingClarification: () => void;
  addClarificationToHistory: (entry: ClarificationHistoryEntry) => void;
  getClarificationHistory: () => ClarificationHistoryEntry[];

  // Actions - Command History (Phase 7.2)
  commandHistory: CommandHistoryEntry[];
  addToHistory: (entry: Omit<CommandHistoryEntry, 'id' | 'timestamp'>) => CommandHistoryEntry;
  getRecentCommands: (limit?: number) => CommandHistoryEntry[];
  getCommandsByType: (componentType: string) => CommandHistoryEntry[];
  getLastSuccessfulCommand: () => CommandHistoryEntry | null;
  clearCommandHistory: () => void;
}

// Pagination state (internal)
let currentPage = 1;
let hasMoreConversations = true;

/**
 * Convert backend AIMessage to frontend ChatMessage
 */
const toFrontendMessage = (msg: AIMessage): ChatMessage => ({
  id: msg.id,
  content: msg.content,
  role: msg.role as 'user' | 'assistant' | 'system',
  timestamp: new Date(msg.timestamp),
  metadata: msg.metadata,
});

/**
 * Deduplicate messages by ID (keep first occurrence to preserve order)
 */
const deduplicateMessages = <T extends { id: string }>(messages: T[]): T[] => {
  const seenIds = new Set<string>();
  return messages.filter(msg => {
    if (seenIds.has(msg.id)) {
      return false;
    }
    seenIds.add(msg.id);
    return true;
  });
};

/**
 * Convert frontend ChatMessage to backend format
 */
const toBackendMessage = (msg: ChatMessage): {
  content: string;
  role: MessageRole;
  id?: string;
  timestamp?: string;
  metadata?: Record<string, any>;
} => ({
  id: msg.id,
  content: msg.content,
  role: msg.role as MessageRole,
  timestamp: msg.timestamp.toISOString(),
  metadata: msg.metadata,
});

/**
 * AI Chat Store
 */
export const useAIChatStore = create<AIChatState>()(
  subscribeWithSelector(
    immer((set, get) => ({
      // Initial state
      conversations: [],
      isLoadingConversations: false,
      conversationsError: null,

      activeConversationId: null,
      activeConversation: null,
      messages: [],
      isLoadingMessages: false,

      inputValue: '',
      isTyping: false,
      error: null,
      contextEnabled: false,
      showConversationList: false,

      isSyncing: false,
      lastSyncedAt: null,
      pendingMessages: [],

      // Clarification state (Phase 1.2)
      pendingClarification: null,
      clarificationHistory: [],
      isAwaitingClarification: false,

      // Command History state (Phase 7.2)
      commandHistory: [],

      // Conversation Management Actions
      loadConversations: async (projectId?: string) => {
        set((state) => {
          state.isLoadingConversations = true;
          state.conversationsError = null;
        });

        try {
          currentPage = 1;
          const response = await conversationApi.listConversations({
            page: 1,
            limit: 20,
            projectId,
          });

          if (response.success) {
            hasMoreConversations = response.meta.pagination.hasNextPage;
            set((state) => {
              state.conversations = response.data;
              state.isLoadingConversations = false;
            });
          }
        } catch (error) {
          set((state) => {
            state.conversationsError = error instanceof Error ? error.message : 'Failed to load conversations';
            state.isLoadingConversations = false;
          });
        }
      },

      loadMoreConversations: async () => {
        if (!hasMoreConversations || get().isLoadingConversations) return;

        set((state) => {
          state.isLoadingConversations = true;
        });

        try {
          currentPage += 1;
          const response = await conversationApi.listConversations({
            page: currentPage,
            limit: 20,
          });

          if (response.success) {
            hasMoreConversations = response.meta.pagination.hasNextPage;
            set((state) => {
              state.conversations = [...state.conversations, ...response.data];
              state.isLoadingConversations = false;
            });
          }
        } catch (error) {
          currentPage -= 1; // Revert on error
          set((state) => {
            state.isLoadingConversations = false;
          });
        }
      },

      createConversation: async (title?: string, projectId?: string) => {
        try {
          const response = await conversationApi.createConversation({
            title: title || 'New Conversation',
            projectId,
          });

          if (response.success) {
            const newConversation = response.data;

            set((state) => {
              // Add to conversations list
              state.conversations.unshift({
                _id: newConversation._id,
                title: newConversation.title,
                status: newConversation.status,
                provider: newConversation.provider,
                aiModel: newConversation.aiModel,
                totalTokens: newConversation.totalTokens,
                totalCost: newConversation.totalCost,
                messageCount: newConversation.messages.length,
                lastMessage: newConversation.messages[newConversation.messages.length - 1] || null,
                createdAt: newConversation.createdAt,
                updatedAt: newConversation.updatedAt,
              });

              // Set as active conversation
              state.activeConversationId = newConversation._id;
              state.activeConversation = newConversation;
              state.messages = newConversation.messages.map(toFrontendMessage);
              state.pendingMessages = [];
            });

            return newConversation._id;
          }

          throw new Error('Failed to create conversation');
        } catch (error) {
          set((state) => {
            state.error = error instanceof Error ? error.message : 'Failed to create conversation';
          });
          throw error;
        }
      },

      switchConversation: async (id: string) => {
        // Sync pending messages before switching
        const { pendingMessages, syncMessages } = get();
        if (pendingMessages.length > 0) {
          await syncMessages();
        }

        set((state) => {
          state.isLoadingMessages = true;
          state.error = null;
        });

        try {
          const response = await conversationApi.getConversation(id);

          if (response.success) {
            set((state) => {
              state.activeConversationId = id;
              state.activeConversation = response.data;
              // Deduplicate messages in case duplicates were stored in the database
              state.messages = deduplicateMessages(response.data.messages.map(toFrontendMessage));
              state.pendingMessages = [];
              state.isLoadingMessages = false;
            });
          }
        } catch (error) {
          set((state) => {
            state.error = error instanceof Error ? error.message : 'Failed to load conversation';
            state.isLoadingMessages = false;
          });
        }
      },

      clearActiveConversation: () => {
        set((state) => {
          state.activeConversationId = null;
          state.activeConversation = null;
          state.messages = [];
          state.pendingMessages = [];
          state.inputValue = '';
        });
      },

      deleteConversation: async (id: string) => {
        try {
          const response = await conversationApi.deleteConversation(id);

          if (response.success) {
            set((state) => {
              state.conversations = state.conversations.filter((c) => c._id !== id);

              // Clear active if it was deleted
              if (state.activeConversationId === id) {
                state.activeConversationId = null;
                state.activeConversation = null;
                state.messages = [];
                state.pendingMessages = [];
              }
            });
          }
        } catch (error) {
          set((state) => {
            state.error = error instanceof Error ? error.message : 'Failed to delete conversation';
          });
        }
      },

      updateConversationTitle: async (id: string, title: string) => {
        try {
          const response = await conversationApi.updateConversation(id, { title });

          if (response.success) {
            set((state) => {
              // Update in conversations list
              const index = state.conversations.findIndex((c) => c._id === id);
              if (index !== -1) {
                state.conversations[index].title = title;
              }

              // Update active conversation if it's the one being updated
              if (state.activeConversation?._id === id) {
                state.activeConversation.title = title;
              }
            });
          }
        } catch (error) {
          set((state) => {
            state.error = error instanceof Error ? error.message : 'Failed to update conversation title';
          });
        }
      },

      archiveConversation: async (id: string) => {
        try {
          const response = await conversationApi.updateConversation(id, {
            status: ConversationStatus.ARCHIVED,
          });

          if (response.success) {
            set((state) => {
              // Update status in conversations list
              const index = state.conversations.findIndex((c) => c._id === id);
              if (index !== -1) {
                state.conversations[index].status = ConversationStatus.ARCHIVED;
              }

              // Clear active if it was archived
              if (state.activeConversationId === id) {
                state.activeConversationId = null;
                state.activeConversation = null;
                state.messages = [];
              }
            });
          }
        } catch (error) {
          set((state) => {
            state.error = error instanceof Error ? error.message : 'Failed to archive conversation';
          });
        }
      },

      // Message Management Actions
      addMessage: (messageData) => {
        const message: ChatMessage = {
          ...messageData,
          id: uuidv4(),
          timestamp: new Date(),
        };

        set((state) => {
          state.messages.push(message);
          state.pendingMessages.push(message);

          // Update conversation summary in list
          if (state.activeConversationId) {
            const index = state.conversations.findIndex(
              (c) => c._id === state.activeConversationId
            );
            if (index !== -1) {
              state.conversations[index].messageCount += 1;
              state.conversations[index].lastMessage = {
                id: message.id,
                role: message.role as MessageRole,
                content: message.content,
                timestamp: message.timestamp.toISOString(),
              };
              state.conversations[index].updatedAt = new Date().toISOString();
            }
          }
        });

        return message;
      },

      updateMessage: (id, updates) => {
        set((state) => {
          const index = state.messages.findIndex((m) => m.id === id);
          if (index !== -1) {
            state.messages[index] = { ...state.messages[index], ...updates };
          }

          // Also update in pending messages if present
          const pendingIndex = state.pendingMessages.findIndex((m) => m.id === id);
          if (pendingIndex !== -1) {
            state.pendingMessages[pendingIndex] = {
              ...state.pendingMessages[pendingIndex],
              ...updates,
            };
          }
        });
      },

      removeMessage: (id) => {
        set((state) => {
          state.messages = state.messages.filter((m) => m.id !== id);
          state.pendingMessages = state.pendingMessages.filter((m) => m.id !== id);
        });
      },

      syncMessages: async () => {
        const { activeConversationId, pendingMessages } = get();

        if (!activeConversationId || pendingMessages.length === 0) {
          return;
        }

        set((state) => {
          state.isSyncing = true;
        });

        try {
          const response = await conversationApi.addMessagesBatch(activeConversationId, {
            messages: pendingMessages.map(toBackendMessage),
          });

          if (response.success) {
            set((state) => {
              state.pendingMessages = [];
              state.lastSyncedAt = new Date();
              state.isSyncing = false;
            });
          } else {
            console.error('❌ [Store] Sync failed - response not successful');
          }
        } catch (error) {
          console.error('❌ [Store] Sync error:', error);
          set((state) => {
            state.isSyncing = false;
            state.error = error instanceof Error ? error.message : 'Failed to sync messages';
          });
        }
      },

      saveMessageToServer: async (message: ChatMessage) => {
        const { activeConversationId } = get();

        if (!activeConversationId) return;

        try {
          await conversationApi.addMessage(activeConversationId, {
            content: message.content,
            role: message.role as MessageRole,
            metadata: message.metadata,
          });

          // Remove from pending messages after successful save
          set((state) => {
            state.pendingMessages = state.pendingMessages.filter((m) => m.id !== message.id);
          });
        } catch (error) {
          console.error('Failed to save message to server:', error);
        }
      },

      // UI State Actions
      setInputValue: (value) => {
        set((state) => {
          state.inputValue = value;
        });
      },

      setError: (error) => {
        set((state) => {
          state.error = error;
        });
      },

      setContextEnabled: (enabled) => {
        set((state) => {
          state.contextEnabled = enabled;
        });
      },

      setIsTyping: (isTyping) => {
        set((state) => {
          state.isTyping = isTyping;
        });
      },

      toggleConversationList: () => {
        set((state) => {
          state.showConversationList = !state.showConversationList;
        });
      },

      clearMessages: () => {
        set((state) => {
          state.messages = [];
          state.pendingMessages = [];
        });
      },

      // Initialization Actions
      initialize: async (projectId?: string) => {
        // Clear existing state before loading new project data
        set((state) => {
          state.activeConversationId = null;
          state.activeConversation = null;
          state.messages = [];
          state.pendingMessages = [];
          state.conversations = [];
        });

        // Load conversations list for this project
        await get().loadConversations(projectId);

        // Try to resume active conversation for this project
        await get().resumeActiveConversation(projectId);
      },

      resumeActiveConversation: async (projectId?: string) => {
        try {
          const response = await conversationApi.getActiveConversation(projectId);

          if (response.success && response.data) {
            set((state) => {
              state.activeConversationId = response.data!._id;
              state.activeConversation = response.data;
              // Deduplicate messages in case duplicates were stored in the database
              state.messages = deduplicateMessages(response.data!.messages.map(toFrontendMessage));
            });
          }
        } catch (error) {
          // No active conversation to resume - this is fine
          console.debug('No active conversation to resume');
        }
      },

      // Clarification Actions (Phase 1.2)
      setPendingClarification: (request: ClarificationRequest | null) => {
        console.log('[AIChatStore] Setting pending clarification:', request?.id || 'null');
        set((state) => {
          state.pendingClarification = request;
          state.isAwaitingClarification = request !== null;
        });
      },

      clearPendingClarification: () => {
        console.log('[AIChatStore] Clearing pending clarification');
        set((state) => {
          state.pendingClarification = null;
          state.isAwaitingClarification = false;
        });
      },

      addClarificationToHistory: (entry: ClarificationHistoryEntry) => {
        console.log('[AIChatStore] Adding clarification to history:', entry.request.id);
        set((state) => {
          state.clarificationHistory.push(entry);
          // Keep only last 20 entries to prevent memory bloat
          if (state.clarificationHistory.length > 20) {
            state.clarificationHistory = state.clarificationHistory.slice(-20);
          }
        });
      },

      getClarificationHistory: () => {
        return get().clarificationHistory;
      },

      // ============================================================================
      // PHASE 7.2: COMMAND HISTORY ACTIONS
      // ============================================================================

      /**
       * Add a command to the history.
       * Called after a command is successfully executed.
       */
      addToHistory: (entry: Omit<CommandHistoryEntry, 'id' | 'timestamp'>) => {
        const historyEntry: CommandHistoryEntry = {
          ...entry,
          id: `cmd_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
          timestamp: Date.now(),
        };

        set((state) => {
          state.commandHistory.push(historyEntry);
          // Keep only the last MAX_COMMAND_HISTORY entries
          if (state.commandHistory.length > MAX_COMMAND_HISTORY) {
            state.commandHistory = state.commandHistory.slice(-MAX_COMMAND_HISTORY);
          }
        });

        return historyEntry;
      },

      /**
       * Get recent commands, optionally limited to a specific count.
       * Returns commands in reverse chronological order (newest first).
       */
      getRecentCommands: (limit = 20) => {
        const history = get().commandHistory;
        // Return in reverse order (newest first) and limit
        const recent = [...history].reverse().slice(0, limit);
        return recent;
      },

      /**
       * Get commands filtered by component type.
       * Useful for showing relevant commands for the current component.
       */
      getCommandsByType: (componentType: string) => {
        const history = get().commandHistory;
        const filtered = history.filter((entry) => entry.componentType === componentType);
        return [...filtered].reverse(); // Newest first
      },

      /**
       * Get the last successful command.
       * Used for "repeat" / "again" / "same" functionality.
       */
      getLastSuccessfulCommand: () => {
        const history = get().commandHistory;
        // Find the last successful command
        for (let i = history.length - 1; i >= 0; i--) {
          if (history[i].success) {
            return history[i];
          }
        }
        return null;
      },

      /**
       * Clear all command history.
       */
      clearCommandHistory: () => {
        set((state) => {
          state.commandHistory = [];
        });
      },
    }))
  )
);

// Export selectors for common patterns
export const selectActiveConversation = (state: AIChatState) => state.activeConversation;
export const selectMessages = (state: AIChatState) => state.messages;
export const selectConversations = (state: AIChatState) => state.conversations;
export const selectIsLoading = (state: AIChatState) =>
  state.isLoadingConversations || state.isLoadingMessages;
export const selectHasActiveConversation = (state: AIChatState) =>
  state.activeConversationId !== null;

// Clarification selectors (Phase 1.2)
export const selectPendingClarification = (state: AIChatState) => state.pendingClarification;
export const selectIsAwaitingClarification = (state: AIChatState) => state.isAwaitingClarification;
export const selectClarificationHistory = (state: AIChatState) => state.clarificationHistory;

// Command History selectors (Phase 7.2)
export const selectCommandHistory = (state: AIChatState) => state.commandHistory;

export default useAIChatStore;
